import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { MobileContextService } from '../../../core/context/services/mobile-context.service';
import { StandaloneAuthService } from '../../../core/auth/services/standalone-auth.service';
import { STORAGE_KEYS, LEGACY_STORAGE_KEYS } from '../../../shared/constants';

/**
 * Serviço de inicialização do PWA (projeto standalone, sem AngularJS).
 *
 * 1. Verifica se há token/contexto disponível e VÁLIDO
 * 2. Se não houver (ou pós-logout explícito), inicia login OAuth2 via StandaloneAuthService
 * 3. Após login, carrega preferências do usuário (my-preferences)
 * 4. Carrega último contexto salvo nas preferências
 * 5. Atualiza o contexto do PWA
 */
@Injectable({
  providedIn: 'root'
})
export class PwaInitializationService {
  /** Evita disparar carregamento de contexto em paralelo (race condition) */
  private loadingContext = false;

  get isLoadingContext(): boolean {
    return this.loadingContext;
  }

  /** Emite quando o carregamento de contexto termina; AppComponent usa pra saber se abre o modal de contexto. */
  private contextLoadDoneSubject = new Subject<void>();
  public contextLoadDone$ = this.contextLoadDoneSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private contextService: MobileContextService,
    private standaloneAuth: StandaloneAuthService
  ) {}

  /** Retorna true se a inicialização foi bem-sucedida, false se redirecionou para login. */
  async checkAndInitialize(): Promise<boolean> {
    // Retorno pós-logout: o AngularJS em /?pwa_logout=true pode ter re-autenticado via SSO
    // residual. Limpar tokens e forçar novo login pra garantir logout efetivo.
    if (sessionStorage.getItem('__pwa_post_logout')) {
      sessionStorage.removeItem('__pwa_post_logout');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('authenticationData');
      sessionStorage.removeItem('bfc-oauth2.authenticationData');
      localStorage.removeItem('access_token');
      localStorage.removeItem('authenticationData');
      localStorage.removeItem('bfc-oauth2.authenticationData');
      localStorage.removeItem(STORAGE_KEYS.BRIDGE_TOKEN);
      localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN); // TODO remover após 1 release
      localStorage.removeItem('__pwa_standalone_token');
      localStorage.removeItem('__pwa_standalone_token_expiry');
      // forcePrompt=true: sem isso o auth server reautentica silenciosamente via cookie de SSO
      // ainda válido e o "logout" vira login automático de novo.
      this.standaloneAuth.login(true);
      return false;
    }

    // Detecta retorno de um fluxo de auth (TWA/Chrome Custom Tab) recente pra dar
    // uma folga antes de checar o token, evitando ler estado ainda não propagado.
    const lastAuthCheck = sessionStorage.getItem('__pwa_last_auth_check');
    const now = Date.now();
    if (lastAuthCheck) {
      const timeSinceLastCheck = now - parseInt(lastAuthCheck, 10);
      if (timeSinceLastCheck < 2000) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    sessionStorage.setItem('__pwa_last_auth_check', now.toString());

    const token = this.getToken();
    // Fallback de leitura na chave antiga (__pwa_bridge_token) por 1 release — TODO remover
    const hasBridgeToken = !!(localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN) ||
      localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN));
    const isAuthenticated = this.standaloneAuth.isAuthenticated();

    if (!token) {
      // Sem token em modo standalone (TWA/browser direto): login OAuth2 direto pelo PWA,
      // sem depender do AngularJS. Limpa tokens residuais pra evitar sessão stale.
      localStorage.removeItem('access_token');
      localStorage.removeItem('authenticationData');
      localStorage.removeItem('bfc-oauth2.authenticationData');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('authenticationData');
      sessionStorage.removeItem('bfc-oauth2.authenticationData');
      this.standaloneAuth.login();
      return false;
    }

    // Chegamos aqui só em modo standalone/TWA (sem AngularJS na página), onde o expiry
    // local é a única fonte de verdade sobre validade do token (bridge ou standalone).
    if (!isAuthenticated) {
      const expiry = localStorage.getItem('__pwa_standalone_token_expiry');
      const hasStandaloneToken = !!localStorage.getItem('__pwa_standalone_token');

      if (!expiry && !hasBridgeToken && !hasStandaloneToken) {
        // Token é resíduo de sessão AngularJS anterior (access_token/authenticationData),
        // sem bridge/standalone token — provavelmente expirado no servidor. Limpar e logar de novo.
        localStorage.removeItem('access_token');
        localStorage.removeItem('authenticationData');
        localStorage.removeItem('bfc-oauth2.authenticationData');
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('authenticationData');
        sessionStorage.removeItem('bfc-oauth2.authenticationData');
        this.standaloneAuth.login();
        return false;
      } else if (!expiry) {
        // Token recém chegou via bridge (1ª sessão híbrida), sem expiry: assume 8h de
        // validade e segue; o interceptor trata 401 se necessário.
        const expiryTime = Date.now() + (8 * 3600 * 1000);
        localStorage.setItem('__pwa_standalone_token_expiry', expiryTime.toString());
        localStorage.setItem('__pwa_standalone_token', token);
      } else {
        // Expiry expirado: token expirou no servidor. Limpar tudo e logar de novo.
        localStorage.removeItem(STORAGE_KEYS.BRIDGE_TOKEN);
        localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN); // TODO remover após 1 release
        localStorage.removeItem('__pwa_standalone_token');
        localStorage.removeItem('__pwa_standalone_token_expiry');
        localStorage.removeItem('access_token');
        localStorage.removeItem('authenticationData');
        localStorage.removeItem('bfc-oauth2.authenticationData');
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('authenticationData');
        sessionStorage.removeItem('bfc-oauth2.authenticationData');
        this.standaloneAuth.login();
        return false;
      }
    }

    const contexto = this.contextService.getContextoAtual();

    // Checa localStorage direto pra evitar chamada HTTP a my-preferences quando já há contexto salvo.
    // Fallback de leitura na chave antiga (__pwa_bridge_contexto_completo) por 1 release — TODO remover
    const hasContextoCompleto = !!(localStorage.getItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO) ||
      localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO));

    if ((!contexto || !contexto.turma) && !hasContextoCompleto) {
      this.contextLoadDoneSubject.next();
    }

    return true;
  }

  /**
   * Token do bridge, chave própria deste domínio (não coexiste com AngularJS, sem fallback
   * pro formato access_token/authenticationData do bfc-oauth2).
   * Fallback de leitura na chave antiga (__pwa_bridge_token) por 1 release — TODO remover
   */
  private getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN) ||
      localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN);
  }

}

